import CommonConstants from '@/constants/common-constants';
import { GET_LIFETIME_PLAN_QUERY } from '@/graphql/pricing.gql';
import { useOrganizationStore } from '@/stores/organization.store';
import { PricingPlan, PricingPlanType } from '@/types/pricing.type';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

export interface LifetimeOfferCountdown {
  hours: number;
  minutes: number;
  seconds: number;
}

export interface LifetimeOffer {
  /** A lifetime plan exists in the pricing tables and is still claimable. */
  isEligible: boolean;
  /** Eligible and past the quiet period, so the popup may be shown. */
  isPopupDue: boolean;
  /** The organization has already bought lifetime. */
  isOwned: boolean;
  msRemaining: number;
  countdown: LifetimeOfferCountdown;
  planType?: PricingPlanType;
  plan?: PricingPlan;
  orgId?: string;
}

function toCountdown(msRemaining: number): LifetimeOfferCountdown {
  const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));

  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60
  };
}

/**
 * Drives the 24 hour lifetime offer: whether it is still claimable for the
 * selected organization, and how long is left.
 *
 * The window runs from `organization.created_at`. The clock is the viewer's,
 * so a user with a skewed system time sees a skewed countdown - the API is
 * still the authority on whether a purchase is allowed, and this only decides
 * what to show.
 */
export function useLifetimeOffer(): LifetimeOffer {
  const { selectedOrganization } = useOrganizationStore();

  // Cache-first by default, so this runs once per session rather than on every
  // navigation, even though the hook is mounted app-wide. The error is
  // swallowed deliberately: a failing offer lookup must never break the page
  // it rides on.
  const { data } = useQuery(GET_LIFETIME_PLAN_QUERY, {
    onError: error => console.error(error)
  });

  const [now, setNow] = useState<number>(() => Date.now());

  // SessionAuth blocks rendering until the organization is fetched, so this is
  // populated by the time any consumer of this hook mounts.
  const createdAt = selectedOrganization?.created_at;
  const createdAtMs = createdAt ? new Date(createdAt).getTime() : Number.NaN;
  const expiresAt = createdAtMs + CommonConstants.LIFETIME_OFFER_WINDOW_MS;

  const planType: PricingPlanType | undefined =
    data?.pricing_versions?.[0]?.pricing_plan_types?.[0];
  const plan: PricingPlan | undefined = planType?.pricing_plans?.[0];

  const isOwned = Boolean(
    selectedOrganization?.pricing_subscription?.pricing_plan?.pricing_plan_type
      ?.is_lifetime
  );

  // An unparseable or missing timestamp must not open the offer forever.
  const hasValidWindow = Number.isFinite(createdAtMs);
  const msRemaining = hasValidWindow ? Math.max(0, expiresAt - now) : 0;

  const isEligible = Boolean(
    hasValidWindow &&
      msRemaining > 0 &&
      !isOwned &&
      planType?.is_lifetime &&
      plan?.pricing_price?.product_id
  );

  const msSinceCreated = hasValidWindow
    ? CommonConstants.LIFETIME_OFFER_WINDOW_MS - msRemaining
    : 0;
  const isPopupDue =
    isEligible &&
    msSinceCreated >= CommonConstants.LIFETIME_OFFER_POPUP_DELAY_MS;

  // Tick only while there is something counting down. Consumers are leaf
  // components, so this re-renders a badge rather than the page behind it.
  useEffect(() => {
    if (!isEligible) {
      return;
    }

    const timer = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(timer);
  }, [isEligible]);

  return {
    isEligible,
    isPopupDue,
    isOwned,
    msRemaining,
    countdown: toCountdown(msRemaining),
    planType,
    plan,
    orgId: selectedOrganization?.id
  };
}
